import React, { useState, useEffect } from 'react';
import { getGlobalMobileApp, updateGlobalMobileApp } from '../services/clientService';
import {
    Smartphone, ExternalLink, Check, AlertCircle, Loader2,
    Trash2, Send, Sparkles, QrCode, Copy, Terminal, ShieldCheck,
    CheckCircle2, RefreshCw, Share
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AppMobile = () => {
    const [url, setUrl] = useState('');
    const [description, setDescription] = useState('');
    const [totalClients, setTotalClients] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', message: string }

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        setLoading(true);
        setFeedback(null);
        try {
            const data = await getGlobalMobileApp();
            setUrl(data.mobile_app_url || '');
            setDescription(data.mobile_app_description || '');
            setTotalClients(data.total_clients || 0);
        } catch (err) {
            console.error('Error fetching global mobile app config:', err);
            setFeedback({ type: 'error', message: 'Erreur lors du chargement de la configuration.' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (customUrl = url, customDesc = description) => {
        setSaving(true);
        setFeedback(null);
        try {
            const res = await updateGlobalMobileApp({
                mobile_app_url: customUrl,
                mobile_app_description: customDesc
            });
            setFeedback({
                type: 'success',
                message: res.message || `Mise à jour diffusée instantanément à tous les établissements.`
            });
        } catch (err) {
            console.error('Error updating global mobile app config:', err);
            setFeedback({ type: 'error', message: 'Échec de la diffusion de la mise à jour.' });
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => {
        if (window.confirm('Voulez-vous désactiver et masquer le bouton Application Mobile chez TOUS les clients ?')) {
            setUrl('');
            setDescription('');
            handleSave('', '');
        }
    };

    const handleCopyUrl = () => {
        if (url) {
            navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="flex-1 bg-slate-50 min-h-screen flex flex-col">
            {/* ── Top Header Toolbar ── */}
            <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-8 py-3.5 flex items-center justify-between shadow-xs">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center shadow-xs">
                        <Smartphone size={18} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-sm font-black text-slate-900 tracking-tight m-0">Application Mobile</h1>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                Diffusion Globale
                            </span>
                        </div>
                        <p className="text-[11px] text-slate-500 font-medium m-0 mt-0.5">
                            Gestion centralisée du fichier APK et des nouveautés pour tous les établissements.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={fetchConfig}
                        disabled={loading || saving}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                        title="Actualiser les données"
                    >
                        <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                        <span>Actualiser</span>
                    </button>
                    {url && (
                        <button
                            type="button"
                            onClick={handleClear}
                            disabled={saving}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-50 border border-rose-200 rounded-lg transition"
                            title="Masquer le bouton chez tous les clients"
                        >
                            <Trash2 size={13} />
                            <span>Désactiver partout</span>
                        </button>
                    )}
                </div>
            </div>

            {/* ── Main Content Area ── */}
            <div className="flex-1 p-8 max-w-7xl w-full mx-auto space-y-6">

                {/* ── Status Banner Card ── */}
                <div className="p-6 rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-slate-900 text-white shadow-md relative overflow-hidden">
                    <div className="absolute right-0 top-0 translate-x-8 -translate-y-8 w-64 h-64 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
                    <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        <div className="space-y-1.5 max-w-2xl">
                            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-md text-[11px] font-black uppercase tracking-wider text-emerald-100">
                                <ShieldCheck size={14} />
                                <span>Réseau Multi-Tenant Connecté</span>
                            </div>
                            <h2 className="text-xl font-black text-white tracking-tight m-0">
                                Diffusion Automatique & Alerte Visuelle
                            </h2>
                            <p className="text-xs text-emerald-100/90 leading-relaxed font-medium m-0">
                                Dès que vous modifiez l'URL ou la description ci-dessous, un signal est envoyé en temps réel à <strong>{totalClients} client{totalClients > 1 ? 's' : ''}</strong>. 
                                Un badge clignotant s'affichera dans l'en-tête admin de chaque établissement pour alerter les utilisateurs de la nouvelle mise à jour.
                            </p>
                        </div>

                        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 shrink-0 text-center min-w-[160px]">
                            <div className="text-2xl font-black text-white">{totalClients}</div>
                            <div className="text-[10px] font-bold text-emerald-200 uppercase tracking-widest mt-0.5">Établissements Actifs</div>
                        </div>
                    </div>
                </div>

                {/* ── Feedback Message ── */}
                <AnimatePresence>
                    {feedback && (
                        <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            className={`p-4 rounded-xl text-xs font-bold flex items-center justify-between gap-3 shadow-xs ${
                                feedback.type === 'success'
                                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                                    : 'bg-rose-50 border border-rose-200 text-rose-800'
                            }`}
                        >
                            <div className="flex items-center gap-2.5">
                                {feedback.type === 'success' ? <CheckCircle2 size={18} className="text-emerald-600" /> : <AlertCircle size={18} className="text-rose-600" />}
                                <span>{feedback.message}</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setFeedback(null)}
                                className="text-slate-400 hover:text-slate-600 text-xs cursor-pointer"
                            >
                                ✕
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ── Grid: Form & Live Preview ── */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

                    {/* Column 1: Configuration Form (7 cols) */}
                    <div className="lg:col-span-7 space-y-6">

                        {/* Card: URL */}
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-4">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs">
                                        1
                                    </div>
                                    <div>
                                        <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 m-0">
                                            Lien de Téléchargement Direct (APK)
                                        </h3>
                                        <p className="text-[11px] text-slate-400 font-medium m-0">
                                            Lien généré par Expo EAS Build ou hébergé sur votre serveur.
                                        </p>
                                    </div>
                                </div>

                                {url && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={handleCopyUrl}
                                            className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg transition"
                                            title="Copier le lien"
                                        >
                                            {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                                            <span>{copied ? 'Copié !' : 'Copier'}</span>
                                        </button>
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 hover:text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg transition"
                                            title="Tester l'URL dans un nouvel onglet"
                                        >
                                            <span>Tester</span>
                                            <ExternalLink size={12} />
                                        </a>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <input
                                    type="url"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="https://expo.dev/artifacts/eas/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx.apk"
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-800 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition outline-none"
                                />
                                <div className="text-[11px] text-slate-400 flex items-center gap-1.5 font-medium">
                                    <Terminal size={12} className="text-slate-500 shrink-0" />
                                    <span>Exemple Expo: <code>eas build --platform android --profile preview</code></span>
                                </div>
                            </div>
                        </div>

                        {/* Card: Description / Notes de mise à jour */}
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-4">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-xs">
                                        2
                                    </div>
                                    <div>
                                        <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 m-0 flex items-center gap-1.5">
                                            <span>Nouveautés de cette Version (Changelog)</span>
                                            <Sparkles size={14} className="text-amber-500" />
                                        </h3>
                                        <p className="text-[11px] text-slate-400 font-medium m-0">
                                            Description affichée dans la boîte de dialogue de chaque établissement.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={5}
                                    placeholder={"✨ Version 2.4.0\n✅ Amélioration de la fluidité et des performances\n✅ Correction du chargement des cours hors-ligne\n🆕 Module de suivi des trajets en temps réel"}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition outline-none resize-y leading-relaxed"
                                />
                                <p className="text-[11px] text-slate-400 font-medium">
                                    Les retours à la ligne sont conservés et mis en valeur chez les utilisateurs.
                                </p>
                            </div>
                        </div>

                        {/* Card: Distribution iOS (PWA) */}
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-4">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-xs">
                                        <Smartphone size={14} />
                                    </div>
                                    <div>
                                        <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 m-0">
                                            Distribution iOS (PWA)
                                        </h3>
                                        <p className="text-[11px] text-slate-400 font-medium m-0">
                                            Application web progressive (sans App Store).
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const pwaUrl = `${window.location.origin}/install`;
                                            navigator.clipboard.writeText(pwaUrl);
                                        }}
                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg transition"
                                        title="Copier le lien"
                                    >
                                        <Copy size={12} />
                                        <span>Copier</span>
                                    </button>
                                    <a
                                        href="/install"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-lg transition"
                                        title="Voir la page d'installation"
                                    >
                                        <span>Ouvrir</span>
                                        <ExternalLink size={12} />
                                    </a>
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-6 items-center md:items-start pt-2">
                                <div className="p-3 bg-white rounded-2xl border-2 border-slate-100 shadow-sm relative group shrink-0">
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(window.location.origin + '/install')}`}
                                        alt="QR Code PWA iOS"
                                        className="w-[120px] h-[120px] object-contain rounded-xl"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <p className="text-xs text-slate-600 leading-relaxed font-medium m-0">
                                        Les utilisateurs iOS peuvent installer l'application en visitant le lien d'installation (<strong>{window.location.origin}/install</strong>) via Safari.
                                    </p>
                                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                                        <p className="text-[11px] font-bold text-slate-800 m-0 mb-1.5 flex items-center gap-1.5">
                                            <Share size={12} className="text-blue-500" />
                                            <span>Procédure "Add to Home Screen"</span>
                                        </p>
                                        <p className="text-[11px] text-slate-500 m-0">
                                            Sur la page d'installation, un tutoriel interactif guide l'utilisateur pour ajouter l'icône sur son écran d'accueil via le menu "Partager" de Safari.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => handleSave()}
                                disabled={saving || !url.trim()}
                                className="px-8 py-3.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-xl shadow-md shadow-emerald-600/20 hover:shadow-lg transition flex items-center gap-2.5 cursor-pointer active:scale-95"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        <span>Diffusion en cours...</span>
                                    </>
                                ) : (
                                    <>
                                        <Send size={16} />
                                        <span>Diffuser à tous les établissements ({totalClients})</span>
                                    </>
                                )}
                            </button>
                        </div>

                    </div>

                    {/* Column 2: Live Client Preview (5 cols) */}
                    <div className="lg:col-span-5 space-y-6">

                        {/* Card: Modal Preview */}
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-4">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                                <div className="flex items-center gap-2">
                                    <QrCode size={16} className="text-emerald-600" />
                                    <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 m-0">
                                        Aperçu Modal Client
                                    </h3>
                                </div>
                                <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                                    Rendu Réel
                                </span>
                            </div>

                            {/* Preview Mockup Box */}
                            <div className="p-5 rounded-2xl bg-gradient-to-b from-slate-50 via-white to-slate-50 border border-slate-200 text-center space-y-4">
                                
                                {/* QR Code Box */}
                                <div className="inline-block p-3.5 bg-white rounded-3xl border-2 border-slate-200 shadow-md relative group">
                                    {url ? (
                                        <img
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(url)}`}
                                            alt="QR Code APK"
                                            className="w-[180px] h-[180px] object-contain rounded-xl mx-auto"
                                        />
                                    ) : (
                                        <div className="w-[180px] h-[180px] flex flex-col items-center justify-center text-slate-300 gap-2 border-2 border-dashed border-slate-200 rounded-xl">
                                            <QrCode size={36} />
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Lien manquant</span>
                                        </div>
                                    )}
                                    <div className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-md border-2 border-white">
                                        <Smartphone size={13} />
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-xs font-black text-slate-900 m-0">Scannez pour installer l'application</h4>
                                    <p className="text-[11px] text-slate-500 font-medium mt-1 mb-0 leading-relaxed max-w-xs mx-auto">
                                        Pointez l'appareil photo Android vers le code QR pour télécharger le build APK.
                                    </p>
                                </div>

                                {/* Changelog preview if present */}
                                {description && (
                                    <div className="text-left p-3.5 rounded-xl bg-amber-50/70 border border-amber-200 text-xs">
                                        <div className="flex items-center gap-1.5 text-amber-700 font-black text-[10px] uppercase tracking-wider mb-1.5">
                                            <Sparkles size={12} />
                                            <span>Nouveautés de cette version</span>
                                        </div>
                                        <div className="text-[11px] text-slate-700 whitespace-pre-line leading-relaxed font-medium">
                                            {description}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Card: Guide Déploiement */}
                        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-3">
                            <h4 className="text-xs font-black uppercase tracking-wider text-slate-800 m-0 flex items-center gap-2">
                                <Terminal size={14} className="text-blue-600" />
                                <span>Workflow de Build Rapide (Expo EAS)</span>
                            </h4>
                            <ol className="text-xs text-slate-600 space-y-2 m-0 pl-4 list-decimal font-medium leading-relaxed">
                                <li>Lancez la commande <code>eas build -p android --profile preview</code> dans votre terminal.</li>
                                <li>Attendez la fin du build sur les serveurs Expo.</li>
                                <li>Copiez le lien direct vers le fichier <strong>.apk</strong> généré.</li>
                                <li>Collez l'URL ici et cliquez sur <strong>Diffuser à tous les établissements</strong>.</li>
                            </ol>
                        </div>

                    </div>

                </div>

            </div>
        </div>
    );
};

export default AppMobile;
